import React, { useContext, useMemo, useCallback } from 'react';
import {
  ProviderContext as NotistackContext,
  useSnackbar as useNotistack,
  SnackbarMessage,
  SnackbarKey,
} from 'notistack';
import { getErrorDetails, type ErrorDetailsOptions } from '../../utils/getErrorDetails';
import { SnackbarContent, AlertSeverity } from './SnackbarContent';
import { SnackbarErrorContent } from './SnackbarErrorContent';

type SnackbarOptions = {
  action?: React.ReactNode;
  preventDuplicate?: boolean;
};

export interface SnackbarContextType {
  closeSnackbar: NotistackContext['closeSnackbar'];
  errorSnackbar: (message: SnackbarMessage, options?: SnackbarOptions) => SnackbarKey;
  exceptionSnackbar: (error: unknown, message: string | ErrorDetailsOptions, options?: SnackbarOptions) => SnackbarKey;
  infoSnackbar: (message: SnackbarMessage, options?: SnackbarOptions) => SnackbarKey;
  successSnackbar: (message: SnackbarMessage, options?: SnackbarOptions) => SnackbarKey;
  warningSnackbar: (message: SnackbarMessage, options?: SnackbarOptions) => SnackbarKey;
}

export const SnackbarContext = React.createContext<SnackbarContextType | undefined>(undefined);

// Helper to create a variant-specific enqueue function
function useEnqueueFunction(
  enqueueSnackbar: NotistackContext['enqueueSnackbar'],
  severity: AlertSeverity
): (message: SnackbarMessage, options?: SnackbarOptions) => SnackbarKey {
  return useCallback(
    (message, options) => {
      return enqueueSnackbar(message, {
        preventDuplicate: options?.preventDuplicate ?? true,
        content: (_, message) => <SnackbarContent severity={severity} message={message} action={options?.action} />,
      });
    },
    [enqueueSnackbar, severity]
  );
}

const ErrorToAlertSeverity: Record<'error' | 'info' | 'warning', AlertSeverity> = {
  error: 'error',
  info: 'info',
  warning: 'warning',
};

/**
 * Hook that provides snackbar methods. Must be used within a SnackbarProvider.
 * This hook should only be used internally by SnackbarProviderInner.
 * For external use, use the context from SnackbarProvider.
 */
export function useSnackbarInternal(): SnackbarContextType {
  const { enqueueSnackbar, closeSnackbar } = useNotistack();

  const successSnackbar = useEnqueueFunction(enqueueSnackbar, 'success');
  const infoSnackbar = useEnqueueFunction(enqueueSnackbar, 'info');
  const warningSnackbar = useEnqueueFunction(enqueueSnackbar, 'warning');
  const errorSnackbar = useEnqueueFunction(enqueueSnackbar, 'error');

  const exceptionSnackbar: SnackbarContextType['exceptionSnackbar'] = useCallback(
    (error, message, options) => {
      const errorDetails = getErrorDetails(
        error as Error,
        typeof message === 'string' ? { severity: 'error', title: message } : message
      );
      return enqueueSnackbar(errorDetails.title, {
        persist: true,
        preventDuplicate: options?.preventDuplicate ?? true,
        content: (key) => (
          <SnackbarErrorContent
            id={key}
            severity={ErrorToAlertSeverity[errorDetails.severity]}
            title={errorDetails.title}
            subtitle={errorDetails.message}
            details={errorDetails.serverMessages}
            closeSnackbar={closeSnackbar}
          />
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar]
  );

  const context: SnackbarContextType = useMemo(
    () => ({
      closeSnackbar,
      errorSnackbar,
      exceptionSnackbar,
      infoSnackbar,
      successSnackbar,
      warningSnackbar,
    }),
    [closeSnackbar, errorSnackbar, exceptionSnackbar, infoSnackbar, successSnackbar, warningSnackbar]
  );

  return context;
}

/**
 * Gets the SnackbarContext and throws if the provider is missing.
 */
export function useSnackbar(): SnackbarContextType {
  const ctx = useContext(SnackbarContext);
  if (ctx === undefined) {
    throw new Error('No SnackbarContext found. Did you forget the SnackbarProvider?');
  }
  return ctx;
}
